import { useEffect, useRef, useState } from 'react';
import * as maplibregl from 'maplibre-gl';
import type { GeoJSONSource } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import maplibreWorkerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url';
import { mad } from '../lib/format';

/**
 * Maps.
 *
 * MapLibre GL drawing OpenFreeMap's vector tiles: no API key, no account, no
 * view limits, and commercial use allowed. OpenStreetMap's own tile servers were
 * the first supplier; they are run by volunteers for editing the map, their
 * policy rules out production apps, and every tile came back as a 403 the week
 * Bedly went live on its own domain.
 *
 * MapLibre is used directly rather than inside Leaflet. The Leaflet adapter gave
 * MapLibre a container zero pixels wide, the style never finished loading, and
 * the base map was a blank rectangle with no error anywhere. One engine, used
 * the way OpenFreeMap documents it, has nothing to fall out of step with.
 *
 * The pins still mean what they meant: a listing's public position is jittered
 * on the server, so it is drawn as the ~300 m area it actually represents — a
 * circle, never a point. Only the host's own wizard gets a precise marker.
 */

/*
 * MapLibre 6 does its tile work in a web worker shipped as a separate file, and
 * has to be told where that file is. Without this the worker never starts and
 * the style waits for it forever. Vite bundles the worker and hands back its URL.
 */
maplibregl.setWorkerUrl(maplibreWorkerUrl);

export type MapPin = {
  id: string;
  lat: number;
  lng: number;
  label?: string;
  priceCentimes?: number | null;
};

/** The base-map supplier. The only line that changes to switch provider. */
const STYLE_URL = 'https://tiles.openfreemap.org/styles/liberty';

/** [lng, lat] — MapLibre's order, the reverse of Leaflet's. */
const MOROCCO_CENTRE: [number, number] = [-6.5, 32.0];

const TERRA = '#B04E33';

/** A circle of `metres` around a point, as a polygon MapLibre can fill. */
function circlePolygon(lat: number, lng: number, metres: number, steps = 64): number[][] {
  const dLat = metres / 111_320;
  const dLng = metres / (111_320 * Math.cos((lat * Math.PI) / 180));
  const ring: number[][] = [];
  for (let i = 0; i <= steps; i += 1) {
    const angle = (i / steps) * 2 * Math.PI;
    ring.push([lng + dLng * Math.cos(angle), lat + dLat * Math.sin(angle)]);
  }
  return ring;
}

/** The price tag or label drawn over a pin. Text, never HTML. */
function pinElement(text: string, clickable: boolean): HTMLElement {
  const element = document.createElement('span');
  element.className = `map-pin${clickable ? ' map-pin--clickable' : ''}`;
  element.textContent = text;
  return element;
}

export function MapView({
  pins,
  centre,
  zoom,
  height = 420,
  precise = false,
  approximateRadiusMetres = 300,
  onPinClick,
  onPinMove,
  interactive = true,
}: {
  pins: MapPin[];
  centre?: { lat: number; lng: number };
  zoom?: number;
  height?: number | string;
  precise?: boolean;
  approximateRadiusMetres?: number;
  onPinClick?: (id: string) => void;
  /**
   * Makes the single precise pin movable: drag it, or tap the map where the
   * place really is. For the host's own wizard — an address lookup lands on
   * the street, not on the door.
   */
  onPinMove?: (lat: number, lng: number) => void;
  interactive?: boolean;
}) {
  const container = useRef<HTMLDivElement | null>(null);
  const map = useRef<maplibregl.Map | null>(null);
  const markers = useRef<maplibregl.Marker[]>([]);
  const [ready, setReady] = useState(false);
  const clickHandler = useRef(onPinClick);
  clickHandler.current = onPinClick;
  const moveHandler = useRef(onPinMove);
  moveHandler.current = onPinMove;

  // Primitive copies, so a parent passing a fresh `centre` object on every
  // render does not rebuild the map each time — the previous version did.
  const centreLat = centre?.lat;
  const centreLng = centre?.lng;

  // Created once per mount.
  useEffect(() => {
    if (!container.current) return;
    const instance = new maplibregl.Map({
      container: container.current,
      style: STYLE_URL,
      center: centreLat != null && centreLng != null ? [centreLng, centreLat] : MOROCCO_CENTRE,
      zoom: zoom ?? (centreLat != null ? 14 : 4.2),
      interactive,
      // Never hijack the page's own scroll wheel.
      scrollZoom: false,
      attributionControl: { compact: true },
    });
    if (interactive) {
      instance.addControl(new maplibregl.NavigationControl({ showCompass: false }), 'top-left');
    }

    /*
     * MapLibre reports failures — a style that will not load, tiles that fail,
     * a worker that cannot start — as events, not exceptions. Without this a
     * broken base map is a silent grey rectangle with a clean console, which is
     * exactly how the last version failed.
     */
    instance.on('error', (event: { error?: unknown }) => console.error('[map]', event.error ?? event));

    instance.on('click', (event: { lngLat: { lat: number; lng: number } }) => {
      moveHandler.current?.(event.lngLat.lat, event.lngLat.lng);
    });

    instance.on('load', () => {
      instance.addSource('areas', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
      instance.addLayer({
        id: 'areas-fill',
        type: 'fill',
        source: 'areas',
        paint: { 'fill-color': TERRA, 'fill-opacity': 0.18 },
      });
      instance.addLayer({
        id: 'areas-line',
        type: 'line',
        source: 'areas',
        paint: { 'line-color': TERRA, 'line-width': 1.5 },
      });
      setReady(true);
    });

    /*
     * A map created while its container is hidden or still being laid out —
     * the search page's map view, a wizard step — measures itself at zero and
     * stays blank. Watching the container keeps it sized to what is on screen.
     */
    const observer = new ResizeObserver(() => instance.resize());
    observer.observe(container.current);

    map.current = instance;
    return () => {
      observer.disconnect();
      for (const marker of markers.current) marker.remove();
      markers.current = [];
      instance.remove();
      map.current = null;
      setReady(false);
    };
    // The initial view is set here; later changes are applied by the effects below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interactive]);

  // Draw the pins, and frame them.
  useEffect(() => {
    const instance = map.current;
    if (!instance || !ready) return;

    for (const marker of markers.current) marker.remove();
    markers.current = [];

    const areas = precise
      ? []
      : pins.map((pin) => ({
          type: 'Feature' as const,
          properties: { id: pin.id },
          geometry: {
            type: 'Polygon' as const,
            coordinates: [circlePolygon(pin.lat, pin.lng, approximateRadiusMetres)],
          },
        }));
    (instance.getSource('areas') as GeoJSONSource | undefined)?.setData({
      type: 'FeatureCollection',
      features: areas,
    });

    for (const pin of pins) {
      const text = precise
        ? (pin.label ?? '📍')
        : pin.priceCentimes != null
          ? mad(pin.priceCentimes)
          : (pin.label ?? '•');
      const clickable = !precise && Boolean(clickHandler.current);
      const element = pinElement(text, clickable);
      if (clickable) {
        element.addEventListener('click', (event) => {
          event.stopPropagation();
          clickHandler.current?.(pin.id);
        });
      }
      const movable = precise && Boolean(moveHandler.current);
      if (movable) element.classList.add('map-pin--draggable');
      const marker = new maplibregl.Marker({
        element,
        anchor: 'bottom',
        offset: [0, -4],
        draggable: movable,
      })
        .setLngLat([pin.lng, pin.lat])
        .addTo(instance);
      if (movable) {
        marker.on('dragend', () => {
          const at = marker.getLngLat();
          moveHandler.current?.(at.lat, at.lng);
        });
      }
      markers.current.push(marker);
    }

    if (pins.length > 1) {
      const bounds = new maplibregl.LngLatBounds();
      for (const pin of pins) bounds.extend([pin.lng, pin.lat]);
      instance.fitBounds(bounds, { padding: 48, maxZoom: 14, duration: 0 });
    } else if (pins.length === 1 && pins[0] && !moveHandler.current) {
      instance.jumpTo({ center: [pins[0].lng, pins[0].lat], zoom: zoom ?? (precise ? 16 : 14) });
    }
  }, [pins, precise, approximateRadiusMetres, zoom, ready]);

  // Follow the centre when the parent moves it (the wizard's address lookup).
  useEffect(() => {
    const instance = map.current;
    if (!instance || centreLat == null || centreLng == null) return;
    instance.jumpTo({ center: [centreLng, centreLat] });
  }, [centreLat, centreLng]);

  return (
    <div
      ref={container}
      className="map-canvas"
      style={{ height: typeof height === 'number' ? `${height}px` : height }}
      role="application"
      aria-label="Carte"
    />
  );
}
